'use client';

import { useState, useCallback } from 'react';
import { useToast } from '@/components/shared/ui/toast';
import { batchInvalidate, type InvalidationType } from './mutations';
import { mutate, type Arguments } from 'swr';

type SWRKey = Arguments;

/**
 * Configuration for delete mutation hooks.
 */
export interface UseDeleteMutationOptions<TId> {
  /**
   * The mutation function (API call).
   * Receives the item ID and returns a promise.
   */
  mutationFn: (id: TId) => Promise<void>;

  /**
   * Business ID for cache invalidation.
   */
  businessId: string;

  /**
   * Cache invalidation types to trigger after success.
   */
  invalidate: InvalidationType[];

  /**
   * SWR cache key of the list to update optimistically.
   * Used for immediate removal from UI.
   */
  listCacheKey?: SWRKey;

  /**
   * Item name for user-friendly messages (e.g., "Payment", "Customer").
   */
  itemName?: string;

  /**
   * Success message override.
   * If not provided, uses "{itemName} deleted" or "Deleted".
   */
  successMessage?: string | ((id: TId) => string);

  /**
   * Error message override.
   */
  errorMessage?: string | ((error: Error) => string);

  /**
   * Callback after successful deletion.
   */
  onSuccess?: (id: TId) => void;

  /**
   * Callback on error.
   */
  onError?: (error: Error) => void;

  /**
   * Delay before showing success toast (ms).
   * Default: 1500ms (1.5s).
   */
  feedbackDelay?: number;

  /**
   * Optional custom optimistic removal function.
   * If not provided, uses generic array filter.
   * Receives the item ID and should update the cache.
   */
  onOptimisticRemove?: (id: TId) => void;
}

export interface UseDeleteMutationReturn<TId> {
  /**
   * Execute the delete mutation.
   */
  mutate: (id: TId) => Promise<void>;

  /**
   * Whether the mutation is currently in progress.
   */
  isLoading: boolean;

  /**
   * The last error that occurred.
   */
  error: Error | null;
}

/**
 * Hook for delete mutations with optimistic UI.
 *
 * Flow:
 * 1. User clicks delete
 * 2. Item disappears immediately from UI
 * 3. Wait 1-2s for smooth UX
 * 4. Show success toast
 * 5. Fire API call in background
 * 6. On success: Invalidate caches
 * 7. On error: Restore item + error message
 *
 * @example
 * ```tsx
 * const { mutate: deletePayment, isLoading } = useDeleteMutation({
 *   mutationFn: (id) => fetch(`/api/payments/${id}`, { method: 'DELETE' }),
 *   businessId,
 *   listCacheKey: swrKeys.salesSummary(businessId, range, today),
 *   itemName: 'Transaction',
 *   invalidate: ['earnings', 'salesSummary', 'payments'],
 *   onOptimisticRemove: (id) => {
 *     mutateSales((current) => {
 *       if (!current) return current;
 *       return {
 *         ...current,
 *         transactions: current.transactions.filter(t => t.id !== id),
 *       };
 *     }, { revalidate: false });
 *   },
 * });
 *
 * // Usage:
 * <Button onClick={() => deletePayment(payment.id)}>Delete</Button>
 * ```
 */
export function useDeleteMutation<TId = string>(
  options: UseDeleteMutationOptions<TId>
): UseDeleteMutationReturn<TId> {
  const {
    mutationFn,
    businessId,
    invalidate,
    listCacheKey,
    itemName = 'Item',
    successMessage,
    errorMessage = `Failed to delete ${itemName.toLowerCase()}. Please try again.`,
    onSuccess,
    onError,
    feedbackDelay = 1500,
    onOptimisticRemove,
  } = options;

  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteMutation = useCallback(
    async (id: TId): Promise<void> => {
      // Prevent concurrent mutations
      if (isLoading) return;

      setIsLoading(true);
      setError(null);

      // Store original data for rollback
      let originalData: unknown;

      try {
        // Apply optimistic removal
        if (onOptimisticRemove) {
          // Use custom removal function
          onOptimisticRemove(id);
        } else if (listCacheKey) {
          // Generic array filter
          await mutate(
            listCacheKey,
            (current: unknown) => {
              originalData = current;
              if (Array.isArray(current)) {
                return current.filter((item: any) => item.id !== id);
              }
              return current;
            },
            { revalidate: false }
          );
        }

        // Start API call in background
        const mutationPromise = mutationFn(id);

        // Wait for feedback delay
        await new Promise((resolve) => setTimeout(resolve, feedbackDelay));

        // Show success toast
        const message =
          typeof successMessage === 'function'
            ? successMessage(id)
            : successMessage || `${itemName} deleted`;
        addToast({ message, type: 'success' });

        // Wait for mutation to complete
        await mutationPromise;

        // Call success callback
        onSuccess?.(id);

        // Invalidate caches in background
        setTimeout(() => {
          batchInvalidate(businessId, invalidate);
        }, 100);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);

        // Rollback optimistic update
        if (listCacheKey && originalData !== undefined) {
          await mutate(listCacheKey, originalData, { revalidate: false });
        }

        // Show error toast
        const message =
          typeof errorMessage === 'function'
            ? errorMessage(error)
            : errorMessage;
        addToast({ message, type: 'error' });

        // Call error callback
        onError?.(error);

        // Don't re-throw - UI already rolled back
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      mutationFn,
      businessId,
      invalidate,
      listCacheKey,
      itemName,
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      feedbackDelay,
      onOptimisticRemove,
      addToast,
    ]
  );

  return {
    mutate: deleteMutation,
    isLoading,
    error,
  };
}
